<script lang="ts">
  import { push } from 'svelte-spa-router';
  import { beforeUpdate } from 'svelte';
  import Loading from '../../../etc/loader.svelte';
  import { loginUser } from '../../../components/auth/LoginUser';
  import RepositoryFactory, { BUNDLE_OPTION, CONTRACT } from '../../../repositories/RepositoryFactory';
  import type { GetBundleOptionDisplayResult, GetBundleOptionResult } from '../../../repositories/bundleoption';
  import type { GetSubContractListResult } from '../../../repositories/contract';
  import MailIspPasswordChangeDetail from './MailIspPasswordChangeDetail.svelte';
  import NotesMailIspParts from './NotesMailIspParts.svelte';
  import MailIspUtil from '../../../util/MailIspUtil';
  import { form, field } from 'svelte-forms';
  import { required, matchField } from 'svelte-forms/validators';

  export let mstServiceId: number;

  const bundleOptionRepository = RepositoryFactory[BUNDLE_OPTION];

  const email = field('email', '', [required()]);
  const emailConfirmation = field('emailConfirmation', '', [matchField(email)]);
  const emailPassword = field('emailPassword', '', [required()]);
  const myForm = form(email, emailConfirmation, emailPassword);

  let mailIspList: GetBundleOptionResult[];
  let bundleOptionDisplay: GetBundleOptionDisplayResult;
  let terminateBundleOption: GetBundleOptionResult;
  let changeMailPasswordBundleOption: GetBundleOptionResult;
  let isRegister = false;
  let disabledApply = false;
  let errorMessage = '';
  let pageStatus: 'INPUT' | 'CONFIRM' | 'COMPLETE' | 'CHANGE_MAIL_PASSWORD' = 'INPUT';
  let terminateDate: string;

  $: isRegisterLoading = false;

  /*
   * 表示前処理
   */
  beforeUpdate(() => {
    // 光コラボ, WITHUNET, 光01以外は表示できないので、それ以外の場合はモーダルを閉じる
    // close()ではloadingのまま終わらないので、起動元画面をリロード
    const avaiableBusinessCodes = ['HIKARI_COLLABO', 'WITHUNET', 'HIKARI_01'];
    if (!avaiableBusinessCodes.includes($loginUser.businessCode)) {
      push('/contract/info/');
      location.reload();
    }
  });

  /*
   * 表示処理
   */
  let promise = initialize();

  async function initialize() {
    // 付帯契約一覧を取得
    let subContractList: GetSubContractListResult[];
    const contractRepository = RepositoryFactory[CONTRACT];
    await Promise.all([
      contractRepository.getSubContractList(),
      bundleOptionRepository.getBundleOptionDisplay({ mstServiceId }),
      bundleOptionRepository.getMailIsp()
    ])
      .then((res) => {
        subContractList = res[0];
        bundleOptionDisplay = res[1];
        mailIspList = res[2];
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
      });

    // 付帯契約が利用中のみ申し込みが可能
    if (
      $loginUser.businessCode !== 'HIKARI_01' &&
      !subContractList?.some((sc) => sc.contractStatusCode === 'RUNNING')
    ) {
      disabledApply = true;
    }
    return { mailIspList, bundleOptionDisplay };
  }

  function initializeRegister() {
    $email.value = '';
    $emailConfirmation.value = '';
    $emailPassword.value = '';
    errorMessage = '';
  }

  function exploreServiceCode() {
    // 表示時に光コラボ, WITHUNET,  光01以外の表示制御をしているため、else句での制御はしない
    if ($loginUser.businessCode === 'HIKARI_COLLABO') {
      return 'MAIL_ISP';
    }
    if ($loginUser.businessCode === 'WITHUNET') {
      return 'WITH_MAIL_ISP';
    }
    if ($loginUser.businessCode === 'HIKARI_01') {
      return 'HIKARI01_MAIL_ISP';
    }
  }

  function exploreEmailDomain() {
    if (['HIKARI_COLLABO', 'WITHUNET'].includes($loginUser.businessCode)) {
      return 'u-pa.jp';
    }
    return 'bb-unext01.jp';
  }

  function displayErrorAlert(status: number) {
    if (status === 403) {
      alert('セッションが切れました。再度ログインしてください。');
      loginUser.logout();
      location.reload();
    } else {
      alert('エラーが発生しました。再度やり直してください。');
    }
  }

  /*
   * 解約
   */
  async function doTerminate(id: number) {
    await bundleOptionRepository
      .terminateBundleOption({
        bundleOptionId: id,
        serviceCode: exploreServiceCode(),
        terminateType: 'INDIVIDUAL'
      })
      .then((res) => {
        terminateDate = res.terminateDate;
        onComplete();
      })
      .catch((error) => {
        displayErrorAlert(error.response.status);
        isRegisterLoading = false;
      });
  }

  /*
   * 申込
   */
  async function doRegister() {
    errorMessage = '';

    let isFormValid = false;
    await myForm.validate().then(() => {
      isFormValid = $myForm.valid;
    });
    if (!isFormValid) {
      if ($myForm.hasError('email.required')) {
        errorMessage = 'メールアドレスを入力してください';
        return;
      }
      // [Note] メールアドレス(再入力)の必須チェックはしない
      // メールアドレスの必須入力と一致チェックにて担保できるため
      if ($myForm.hasError('emailPassword.required')) {
        errorMessage = 'パスワードを入力してください';
        return;
      }
    }
    if (!MailIspUtil.isValidUserNameLength($email.value)) {
      errorMessage = 'メールアドレスは1文字以上30文字以内で入力してください';
      return;
    }

    if (!MailIspUtil.isValidPasswordLength($emailPassword.value)) {
      errorMessage = 'パスワードは3文字以上16文字以内で入力してください';
      return;
    }

    if (!MailIspUtil.isAvailableUserNameCharacters($email.value)) {
      errorMessage = 'メールアドレスに使用できない文字が入力されています';
      return;
    }

    if (!MailIspUtil.isAvailablePasswordCharacters($emailPassword.value)) {
      errorMessage = 'パスワードに使用できない文字が入力されています';
      return;
    }

    if ($myForm.hasError('emailConfirmation.match_field')) {
      errorMessage = 'メールアドレスとメールアドレス(再入力)が一致しません';
      return;
    }

    isRegisterLoading = true;

    await bundleOptionRepository
      .registerBundleOptionFreebit({
        serviceCode: exploreServiceCode(),
        email: $email.value,
        emailPassword: $emailPassword.value
      })
      .then((res) => {
        onComplete();
      })
      .catch((error) => {
        if (error.response.status === 411) {
          errorMessage = '既に登録されているアカウントです。';
        } else if (error.response.status === 403) {
          alert('セッションが切れました。再度ログインしてください。');
          loginUser.logout();
          location.reload();
        } else {
          errorMessage = '申し込みでエラーが発生しました。しばらく経ってから再度実行してください。';
          disabledApply = true;
        }
        isRegisterLoading = false;
      });
  }

  /*
   * 画面遷移
   */
  function onConfirmRegister() {
    pageStatus = 'CONFIRM';
    isRegister = true;
    changeMailPasswordBundleOption = null;
    terminateBundleOption = null;
  }

  function onChangeMailPassword(bundleOption: GetBundleOptionResult) {
    pageStatus = 'CHANGE_MAIL_PASSWORD';
    isRegister = false;
    changeMailPasswordBundleOption = bundleOption;
    terminateBundleOption = null;
  }

  function onConfirmTerminate(bundleOption: GetBundleOptionResult) {
    pageStatus = 'CONFIRM';
    isRegister = false;
    changeMailPasswordBundleOption = null;
    terminateBundleOption = bundleOption;
  }

  function onComplete() {
    isRegisterLoading = false;
    pageStatus = 'COMPLETE';
  }

  function onTransitionTop() {
    promise = initialize();
    initializeRegister();
    pageStatus = 'INPUT';
  }

  function getWebmailUrl(key: string) {
    if ($loginUser.businessCode === 'HIKARI_01') {
      return 'http://wm.bb-unext01.jp/';
    } else {
      if (key === 'u-pa.jp') {
        return 'https://wm.u-pa.jp/';
      } else {
        return 'https://wm01.withu.ne.jp/';
      }
    }
  }
</script>

<!-- 入力画面 -->
{#if pageStatus === 'INPUT'}
  {#await promise}
    <Loading />
  {:then { mailIspList, bundleOptionDisplay }}
    <div class="h-auto p-6 bg-white m-auto max-w-4xl">
      <div class="mb-6">
        <div class="container flex justify-between">
          <h1 class="text-2xl font-bold text-gray-700">
            {bundleOptionDisplay.serviceName}
          </h1>
        </div>
      </div>

      <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
        <div class="flex flex-col">
          {#if mailIspList}
            {#each mailIspList as mailIsp}
              <div class="grid grid-cols-3 justify-items-center items-center min-w-full p-4">
                <div class="grid-1 col-span-2 justify-self-start w-full">
                  <div class="grid-1 col-span-2 self-center">
                    <p>
                      メールアドレス：{mailIsp.outboundKey1}@{mailIsp.outboundKey3}
                    </p>
                  </div>
                  <div class="grid-1 col-span-2 self-center">
                    <p>パスワード：{mailIsp.outboundKey2}</p>
                  </div>
                  <div class="grid-1 col-span-2 self-center">
                    <p>ステータス：{mailIsp.contractStatusCode}</p>
                  </div>
                  <div class="grid-1 col-span-2 inline-flex self-center">
                    <p>申込日：{mailIsp.contractStartDate}</p>
                  </div>
                  <div class="grid-1 col-span-2 inline-flex self-center">
                    <p>webmail URL：{getWebmailUrl(mailIsp.outboundKey3)}</p>
                  </div>
                </div>
                <div class="grid-1">
                  <div class="flex flex-col space-y-4 justify-center">
                    <div class="flex-1 content-center">
                      <button on:click={() => onChangeMailPassword(mailIsp)} class="button-change-password w-40">
                        パスワード変更
                      </button>
                    </div>
                    <div class="flex-1 content-center">
                      <button on:click={() => onConfirmTerminate(mailIsp)} class="button-withdrawal w-40">
                        解約
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            {/each}
            <hr class="h-px my-4 bg-gray-300 border-0" />
          {/if}

          <div class="flex-1 p-4 mb-5">
            <ul class="list-none">
              <li>
                <p>{bundleOptionDisplay.serviceName} を申し込みますか？</p>
              </li>
              <li>
                <p>月額：{bundleOptionDisplay.amountWithTax}円（税込み）</p>
              </li>
            </ul>
          </div>

          <div class="flex-1 mb-10">
            <NotesMailIspParts />
          </div>
          <div class="flex-1">
            <div class="flex flex-col justify-items-center items-center">
              <div class="flex-1">
                <button on:click|once={() => onConfirmRegister()} class="button-primary" disabled={disabledApply}>
                  申込手続きへ
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  {/await}
  <!-- 確認画面・完了画面-->
{:else if pageStatus === 'CONFIRM' || pageStatus === 'COMPLETE'}
  <div class="h-auto p-6 bg-white m-auto max-w-4xl">
    <div class="mb-6">
      <div class="container flex justify-between">
        <h1 class="text-2xl font-bold text-gray-700">
          {bundleOptionDisplay.serviceName}
        </h1>
      </div>
    </div>
    <!-- 確認画面-->
    {#if pageStatus === 'CONFIRM'}
      {#if isRegisterLoading}
        <Loading />
      {/if}
      <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
        <div class="flex flex-col">
          {#if isRegister}
            <!-- 新規申込入力  解約の確認画面と同じためCONFRIMで処理している -->
            <div class="flex-1 p-4 mb-4">
              <ul class="list-none">
                <li>
                  <p>申込：{bundleOptionDisplay.serviceName}</p>
                </li>
                <li>
                  <p>月額：{bundleOptionDisplay.amountWithTax}円（税込み）</p>
                </li>
              </ul>
            </div>
            {#if errorMessage}
              <div class="bg-red-100 border-red-500 text-red-700 px-4 py-2 mb-2" role="alert">
                <p class="text-sm font-semibold">{errorMessage}</p>
              </div>
            {/if}
            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center mx-auto">
                <div class="shadow rounded bg-gray-50 w-full content-center p-4">
                  <div class="inline-grid grid-cols-5 justify-items-center items-center min-w-full p-2">
                    <div class="grid-1 col-span-2 justify-self-start w-full">
                      <span class="label-title required">メールアドレス</span>
                    </div>
                    <div class="grid-1 col-span-3 justify-self-start w-full">
                      <input
                        id="email"
                        type="email"
                        placeholder="xxxx"
                        autocomplete="off"
                        maxlength="30"
                        bind:value={$email.value}
                      />
                      <span class="label-title">@{exploreEmailDomain()}</span>
                    </div>
                  </div>
                  <div class="inline-grid grid-cols-5 justify-items-center items-center min-w-full p-2">
                    <div class="grid-1 col-span-2 justify-self-start w-full">
                      <span class="label-title required">メールアドレス（再入力）</span>
                    </div>
                    <div class="grid-1 col-span-3 justify-self-start w-full">
                      <input
                        id="emailConfirmation"
                        type="email"
                        placeholder="xxxx"
                        autocomplete="off"
                        maxlength="30"
                        bind:value={$emailConfirmation.value}
                      />
                      <span class="label-title">@{exploreEmailDomain()}</span>
                    </div>
                  </div>
                  <div class="inline-grid grid-cols-5 justify-items-center items-center min-w-full p-2">
                    <div class="grid-1 col-span-2 justify-self-start w-full">
                      <span class="label-title required">パスワード</span>
                    </div>
                    <div class="grid-1 col-span-3 justify-self-start w-full">
                      <input id="emailPassword" type="password" bind:value={$emailPassword.value} />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="flex-1 my-10">
              <NotesMailIspParts />
            </div>
            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center">
                <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
                  <button class="button-secondary md:ml-5 mb-5 w-40" on:click={onTransitionTop}> 戻る </button>
                  <button on:click={doRegister} class="button-primary md:ml-5 mb-5 w-40" disabled={disabledApply}>
                    申し込む
                  </button>
                </div>
              </div>
            </div>
          {:else}
            <!-- 解約 -->
            <div class="flex-1 p-4 mb-10">
              <ul class="list-none">
                <li>
                  <p>解約：{terminateBundleOption.serviceName}</p>
                </li>
                <li>
                  <p>
                    メールアドレス：{terminateBundleOption.outboundKey1}@{terminateBundleOption.outboundKey3}
                  </p>
                </li>
              </ul>
            </div>
            <div class="flex-1 mb-10">
              <NotesMailIspParts />
            </div>
            <div class="flex-1">
              <div class="flex flex-col justify-items-center items-center">
                <div class="flex-1 m-5 flex flex-col md:flex-row justify-items-center items-center">
                  <button class="button-secondary md:ml-5 mb-5 w-40" on:click={onTransitionTop}> 戻る </button>
                  <button
                    on:click|once={() => doTerminate(terminateBundleOption.bundleOptionId)}
                    class="button-withdrawal md:ml-5 mb-5 w-40"
                  >
                    解約する
                  </button>
                </div>
              </div>
            </div>
          {/if}
        </div>
      </div>
    {:else if pageStatus === 'COMPLETE'}
      <div class="shadow rounded bg-white border-t-2 border-green-400 p-4">
        <div class="flex flex-col">
          <div class="flex-1 mt-4 mb-10">
            <div class="inline-flex justify-items-center items-center min-w-full">
              {#if isRegister}
                申し込みが完了しました。
              {:else}
                解約日：{terminateDate}
              {/if}
            </div>
          </div>

          <div class="flex-1">
            <div class="flex flex-col justify-items-center items-center">
              <div class="flex-1">
                <button on:click|once={onTransitionTop} class="button-secondary"> 契約中メール一覧へ </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    {/if}
  </div>
{:else if pageStatus === 'CHANGE_MAIL_PASSWORD'}
  <MailIspPasswordChangeDetail props={changeMailPasswordBundleOption} />
{/if}

<style lang="postcss">
  .required:after {
    content: ' *';
    color: red;
  }
  p:not([class]) {
    @apply tracking-normal mb-1;
  }
  .label-title {
    @apply text-sm font-medium text-gray-700;
  }

  .button-primary {
    @apply bg-green-400 rounded-lg hover:bg-green-500 focus:outline-none text-sm font-semibold text-white px-10 py-2;
  }
  .button-primary:disabled {
    @apply opacity-30 cursor-default;
  }
  .button-secondary {
    @apply bg-gray-300 rounded-lg hover:bg-gray-400 text-gray-500 text-sm font-semibold focus:outline-none px-10 py-2;
  }
  .button-withdrawal {
    @apply bg-red-100 rounded-lg hover:bg-red-200 text-sm font-semibold focus:outline-none text-red-700 px-6 py-2;
  }
  .button-change-password {
    @apply bg-green-400 rounded-lg hover:bg-green-500 text-sm font-semibold focus:outline-none text-white  px-6 py-2;
  }

  input[type='email'],
  [type='password'] {
    @apply rounded border py-2 px-3 text-sm placeholder-gray-400 focus:placeholder-gray-200 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent;
  }
</style>
